import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { HelpCircle, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '../App';

const MARKETPLACES = [
  { value: 'amazon', label: 'Amazon' },
  { value: 'ebay', label: 'eBay' },
  { value: 'aliexpress', label: 'AliExpress' },
  { value: 'temu', label: 'TEMU' },
  { value: 'shein', label: 'SHEIN' },
  { value: 'walmart', label: 'Walmart' },
  { value: 'alibaba', label: 'Alibaba' },
  { value: 'other', label: 'Other / Not sure' },
];

export default function HelpRequestCard({ variant = 'default', className = '' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!productName.trim()) {
      toast.error('Please describe what you\'re looking for');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/help-requests`,
        {
          product_name: productName.trim(),
          desired_quantity: quantity ? parseInt(quantity) : null,
          budget_range: budgetRange.trim() || null,
          preferred_marketplace: marketplace || null,
          notes: notes.trim() || null,
        },
        { withCredentials: true }
      );
      
      setSubmitted(true);
      toast.success('Help request submitted! We\'ll get back to you soon.');
      
      // Reset after delay
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setProductName('');
        setQuantity('');
        setBudgetRange('');
        setMarketplace('');
        setNotes('');
      }, 2000);
    } catch (error) {
      console.error('Help request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cardContent = (
    <div className={`bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
          <HelpCircle className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900 mb-1">Can&apos;t find the link?</h4>
          <p className="text-sm text-slate-600 mb-3">
            Tell us what you want and we&apos;ll help you find options.
          </p>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="request-help-btn"
              >
                Request Help
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  Request Help Finding a Product
                </DialogTitle>
              </DialogHeader>
              
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Request Submitted!</h3>
                  <p className="text-slate-600">We&apos;ll review your request and get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="product_name">What are you looking for? *</Label>
                    <Input
                      id="product_name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="e.g., Sony WH-1000XM5 headphones"
                      required
                      data-testid="help-product-name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="quantity">Quantity (optional)</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="1"
                        data-testid="help-quantity"
                      />
                    </div>
                    <div>
                      <Label htmlFor="budget">Budget Range (optional)</Label>
                      <Input
                        id="budget"
                        value={budgetRange}
                        onChange={(e) => setBudgetRange(e.target.value)}
                        placeholder="$50-100"
                        data-testid="help-budget"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="marketplace">Preferred Marketplace (optional)</Label>
                    <Select value={marketplace} onValueChange={setMarketplace}>
                      <SelectTrigger data-testid="help-marketplace">
                        <SelectValue placeholder="Any marketplace" />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKETPLACES.map((mp) => (
                          <SelectItem key={mp.value} value={mp.value}>
                            {mp.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="notes">Additional Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any specific requirements, colors, sizes, etc."
                      rows={3}
                      data-testid="help-notes"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="submit-help-request"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    We&apos;ll review your request and suggest options within 24-48 hours.
                  </p>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
        <HelpCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <span className="text-sm text-slate-700">Can&apos;t find it?</span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button 
              size="sm" 
              variant="link"
              className="text-blue-600 p-0 h-auto"
              data-testid="request-help-btn-compact"
            >
              Request Help
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-blue-600" />
                Request Help Finding a Product
              </DialogTitle>
            </DialogHeader>
            
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Request Submitted!</h3>
                <p className="text-slate-600">We&apos;ll review your request and get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="product_name_compact">What are you looking for? *</Label>
                  <Input
                    id="product_name_compact"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Sony WH-1000XM5 headphones"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quantity_compact">Quantity</Label>
                    <Input
                      id="quantity_compact"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget_compact">Budget</Label>
                    <Input
                      id="budget_compact"
                      value={budgetRange}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      placeholder="$50-100"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="marketplace_compact">Marketplace</Label>
                  <Select value={marketplace} onValueChange={setMarketplace}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKETPLACES.map((mp) => (
                        <SelectItem key={mp.value} value={mp.value}>
                          {mp.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="notes_compact">Notes</Label>
                  <Textarea
                    id="notes_compact"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any specific requirements..."
                    rows={2}
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Submit Request
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return cardContent;
}
